import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { msmartAxios } from "../../../api/axios";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../utils/errorMessage";

function formatLocalYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function format12h(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function isFuture(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() > Date.now();
}

function SkeletonRows({ rows = 10 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          <td><div className="skeleton-box" style={{ height: 14, width: 24 }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: "55%" }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: "60%" }} /></td>
          <td><div className="skeleton-box" style={{ height: 14, width: "70%" }} /></td>
          <td><div className="skeleton-box" style={{ height: 32, width: 90, borderRadius: 12 }} /></td>
        </tr>
      ))}
    </>
  );
}

export default function TeamFollowUp() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError, notifySuccess } = useToast();

  const [team, setTeam] = useState([]);
  const [leadRaw, setLeadRaw] = useState([]); // response.data.lead (nested array possible)
  const [loading, setLoading] = useState(true);

  // UI states
  const today = useMemo(() => {
    const t = new Date();
    t.setMinutes(t.getMinutes() - t.getTimezoneOffset());
    return t;
  }, []);
  const todayStr = useMemo(() => today.toISOString().split("T")[0], [today]);

  const [filterDate, setFilterDate] = useState({
    startDate: todayStr,
    endDate: todayStr,
  });
  const [searchName, setSearchName] = useState("");
  const [onlyFuture, setOnlyFuture] = useState(true);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;

  // modal edit remark
  const [selectedLead, setSelectedLead] = useState(null);
  const [editedRemark, setEditedRemark] = useState("");
  const [saving, setSaving] = useState(false);

  const getNameInTeam = useCallback(
    (username) => {
      const member = team.find((m) => String(m.username) === String(username));
      return member ? member.nameInTeam : "-";
    },
    [team]
  );

  const fetchFollowUpData = useCallback(
    async (startDate, endDate) => {
      setLoading(true);
      try {
        const res = await msmartAxios.post(
          `/api/msmart/manager/get/followup/${teamId}`,
          { startDate, endDate },
          { headers: { accessToken: user.token } }
        );

        setLeadRaw(res?.data?.lead || []);
        setTeam(res?.data?.team || []);
        setCurrentPage(1);
      } catch (err) {
        notifyError(getErrorMessage(err, "Unable to fetch follow up."));
        setLeadRaw([]);
        setTeam([]);
      } finally {
        setLoading(false);
      }
    },
    [teamId, user.token, notifyError]
  );

  useEffect(() => {
    if (filterDate.startDate && filterDate.endDate) {
      fetchFollowUpData(filterDate.startDate, filterDate.endDate);
    }
  }, [filterDate.startDate, filterDate.endDate, fetchFollowUpData]);

  const handleDateChange = (selectedDates) => {
    if (selectedDates?.length === 2) {
      const [start, end] = selectedDates;
      setFilterDate({
        startDate: formatLocalYMD(start),
        endDate: formatLocalYMD(end),
      });
    }
  };

  const allRows = useMemo(() => {
    // leadRaw boleh jadi nested arrays
    if (Array.isArray(leadRaw)) {
      return leadRaw.flatMap((x) => (Array.isArray(x) ? x : [x]));
    }
    return [];
  }, [leadRaw]);

  const filteredRows = useMemo(() => {
    const q = searchName.trim().toLowerCase();

    return allRows
      .filter((item) => {
        if (onlyFuture && !isFuture(item.followUpDate)) return false;

        if (!q) return true;
        const memberName = getNameInTeam(item.username).toLowerCase();
        const leadName = String(item.name || "").toLowerCase();
        return memberName.includes(q) || leadName.includes(q);
      })
      .sort((a, b) => new Date(a.followUpDate) - new Date(b.followUpDate)); // soonest first
  }, [allRows, searchName, onlyFuture, getNameInTeam]);

  // KPI
  const kpi = useMemo(() => {
    const total = filteredRows.length;
    const future = filteredRows.filter((x) => isFuture(x.followUpDate)).length;

    const dueToday = filteredRows.filter((x) => {
      const d = new Date(x.followUpDate);
      if (Number.isNaN(d.getTime())) return false;

      const now = new Date();
      return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      );
    }).length;

    return { total, future, dueToday };
  }, [filteredRows]);

  // pagination
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  const currentRows = useMemo(() => {
    const start = (safePage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, safePage]);

  const changePage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  // modal open
  const openLeadModal = (item) => {
    setSelectedLead(item);
    setEditedRemark(""); // default kosong, user isi remark baru
  };

  const closeModalReset = () => {
    setSelectedLead(null);
    setEditedRemark("");
    setSaving(false);
  };

  const saveEditedRemark = async () => {
    if (!selectedLead) return;

    if (!editedRemark.trim()) {
      notifyError("Remark kosong bro. Isi dulu.");
      return;
    }

    setSaving(true);

    const payload = {
      remark: selectedLead.remark,
      updatedRemark: editedRemark,
      remarkDate: new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };

    try {
      const res = await msmartAxios.put(
        `/api/msmart/manager/edit/remark/${teamId}/${selectedLead.id}`,
        payload,
        { headers: { accessToken: user.token } }
      );

      if (res?.data?.succ) {
        notifySuccess(res.data.succ);

        // close modal bootstrap (button id)
        document.getElementById("teamfu_closemodal")?.click();

        // refresh data
        await fetchFollowUpData(filterDate.startDate, filterDate.endDate);
        closeModalReset();
      } else {
        notifyError(res?.data?.error || "Unable to update remark");
      }
    } catch (err) {
      notifyError(getErrorMessage(err, "Unable to update remark"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-6 ss-page">
      {/* Header */}
      <div className="row justify-content-center text-start">
        <div className="col-lg-12">
          <h1 className="header-title mb-1">M-Smart</h1>
          <div className="text-muted" style={{ fontSize: 14 }}>
          Sales Activity Management.
          </div>
        </div>
      </div>

      {/* Segment */}
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="msmart-segment my-4">
            <NavLink to="#" className={() => "msmart-seg-link active"}>
              Team
            </NavLink>
            <NavLink
              to={`/msmart/manager/database/${teamId}`}
              className={({ isActive }) => `msmart-seg-link ${isActive ? "active" : ""}`}
            >
              Personal
            </NavLink>
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="card text-center mt-4 ds-card">
        {/* Tabs */}
        <div className="card-header text-start">
          <ul className="nav nav-tabs card-header-tabs flex-wrap">
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/summary/${teamId}`}>
                Summary
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/summary/${teamId}?view=analysis`}>Analysis</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/manage/${teamId}`}>
                Manage
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/activity/${teamId}`}>
                Activities
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link active" to="#">
                Follow Up
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/team/leads/${teamId}`}>
                Leads
              </Link>
            </li>
          </ul>
        </div>

        <div className="card-body text-start">
          {/* Toolbar */}
          <div className="card mb-3 followup-toolbar">
            <div className="card-body p-3">
              <div className="d-flex flex-column flex-lg-row gap-2 justify-content-between align-items-lg-center">
                <div className="d-flex flex-column flex-md-row gap-2 flex-grow-1">
                  <div className="form-floating flex-grow-1">
                    <Flatpickr
                      id="teamFuDateRange"
                      className="form-control"
                      placeholder="Select date range"
                      value={
                        filterDate.startDate && filterDate.endDate
                          ? [new Date(filterDate.startDate), new Date(filterDate.endDate)]
                          : []
                      }
                      options={{ mode: "range", dateFormat: "d-m-Y", disableMobile: true }}
                      onChange={handleDateChange}
                    />
                    <label htmlFor="teamFuDateRange">Date Range</label>
                  </div>

                  <div className="form-floating flex-grow-1">
                    <input
                      type="text"
                      className="form-control"
                      id="teamFuSearch"
                      placeholder="Search by member / lead"
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                    <label htmlFor="teamFuSearch">Search (Member / Lead)</label>
                  </div>
                </div>

                <div className="d-flex gap-2 justify-content-end align-items-center">
                  <button
                    type="button"
                    className={`btn btn-sm ${onlyFuture ? "btn-dark" : "btn-outline-dark"}`}
                    onClick={() => {
                      setOnlyFuture((v) => !v);
                      setCurrentPage(1);
                    }}
                    title="Only show follow ups after current time"
                  >
                    <i className="bi bi-clock-history me-1" />
                    {onlyFuture ? "Future Only" : "All in Range"}
                  </button>

                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => fetchFollowUpData(filterDate.startDate, filterDate.endDate)}
                    disabled={loading}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise" />
                  </button>
                </div>
              </div>

              {/* KPI chips */}
              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="ds-chip">
                  <i className="bi bi-list-check" /> Total: <b>{kpi.total}</b>
                </span>
                <span className="ds-chip">
                  <i className="bi bi-calendar2-week" /> Due Today: <b>{kpi.dueToday}</b>
                </span>
                <span className="ds-chip ds-chip-dark">
                  <i className="bi bi-alarm" /> Future: <b>{kpi.future}</b>
                </span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive" style={{ fontSize: "0.9rem" }}>
            <table className="table table-bordered table-hover align-middle followup-table">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th className="text-start" style={{ minWidth: 180 }}>Member</th>
                  <th className="text-start" style={{ minWidth: 200 }}>Lead</th>
                  <th style={{ width: 210 }}>Follow Up Time</th>
                  <th style={{ width: 120 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <SkeletonRows rows={10} />
                ) : currentRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      No Follow Up found.
                    </td>
                  </tr>
                ) : (
                  currentRows.map((item, idx) => {
                    const member = getNameInTeam(item.username);
                    const fu = format12h(item.followUpDate);
                    const future = isFuture(item.followUpDate);

                    return (
                      <tr key={item.id}>
                        <td>{idx + 1 + (safePage - 1) * rowsPerPage}</td>

                        <td className="text-start">
                          <div className="fw-semibold">{member}</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            @{item.username}
                          </div>
                        </td>

                        <td className="text-start">
                          <div className="fw-semibold">{item.name || "-"}</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {(item.country || "") + (item.phone || "")}
                          </div>
                        </td>

                        <td>
                          <span className={`badge rounded-pill ${future ? "bg-dark" : "bg-secondary"}`}>
                            {fu}
                          </span>
                        </td>

                        <td>
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-dark"
                              onClick={() => openLeadModal(item)}
                              data-bs-toggle="modal"
                              data-bs-target="#teamFollowUpDetail"
                              title="Details"
                            >
                              <i className="bi bi-list" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-center align-items-center p-2">
            <button
              className="btn btn-sm btn-light me-2"
              onClick={() => changePage(safePage - 1)}
              disabled={loading || safePage === 1}
            >
              <i className="bi bi-caret-left" />
            </button>

            <span className="text-muted" style={{ fontSize: "0.9rem" }}>
              Page <b>{safePage}</b> of <b>{totalPages}</b>
            </span>

            <button
              className="btn btn-sm btn-light ms-2"
              onClick={() => changePage(safePage + 1)}
              disabled={loading || safePage === totalPages}
            >
              <i className="bi bi-caret-right" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal - Lead Details + Add Remark */}
      <div
        className="modal fade"
        id="teamFollowUpDetail"
        tabIndex="-1"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content ss-modal ds-modal">
            <div className="modal-header ss-modal-header">
              <div>
                <div className="fw-bold">Lead Details</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Update remark untuk follow up team.
                </div>
              </div>

              <button
                type="button"
                id="teamfu_closemodal"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={closeModalReset}
              />
            </div>

            {!selectedLead ? (
              <div className="modal-body text-start">
                <p className="text-muted mb-0">No details available.</p>
              </div>
            ) : (
              <>
                <div className="modal-body text-start">
                  {/* quick info */}
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <div>
                      <div className="fw-bold">{selectedLead.name || "-"}</div>
                      <div className="text-muted">
                        {(selectedLead.country || "") + (selectedLead.phone || "")}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Member: <b>{getNameInTeam(selectedLead.username)}</b> (@{selectedLead.username})
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="row g-2">
                    <div className="col-md-6">
                      <div className="small text-muted">Status</div>
                      <div className="fw-semibold">{selectedLead.status || "N/A"}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="small text-muted">Follow Up Date</div>
                      <div className="fw-semibold">
                        {format12h(selectedLead.followUpDate)}
                        {isFuture(selectedLead.followUpDate) ? (
                          <span className="badge bg-dark ms-2">Future</span>
                        ) : (
                          <span className="badge bg-secondary ms-2">Past</span>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Created At</div>
                      <div>{format12h(selectedLead.createdAt)}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="small text-muted">Last Update</div>
                      <div>{format12h(selectedLead.updatedAt)}</div>
                    </div>
                  </div>

                  {/* existing remark */}
                  <div className="mt-3">
                    <div className="small text-muted mb-1">Current Remark</div>
                    <div className="card">
                      <div className="card-body" style={{ whiteSpace: "pre-wrap" }}>
                        {selectedLead.remark ? selectedLead.remark : <span className="text-muted">No Remark</span>}
                      </div>
                    </div>
                  </div>

                  {/* add new remark */}
                  <div className="form-floating mt-3">
                    <textarea
                      id="teamFuNewRemark"
                      className="form-control"
                      style={{ height: 140 }}
                      placeholder="Type your remark..."
                      value={editedRemark}
                      onChange={(e) => setEditedRemark(e.target.value)}
                    />
                    <label htmlFor="teamFuNewRemark">Your Remark (New)</label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    data-bs-dismiss="modal"
                    onClick={closeModalReset}
                    disabled={saving}
                  >
                    Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={saveEditedRemark}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2" />
                        Save Remark
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
